import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  ReferenceLine,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Spinner, ErrorDisplay, EmptyState } from "@/components/loading";
import {
  buildCharts,
  fetchPlotRecordsWithMeta,
  pad2,
  rangeLabel,
  type ChartSpec,
  type ChartValueFormat,
  type PlotFilters,
} from "./build-charts";
import { LocationHeatmapCard } from "./location-heatmap";

const DAY_MS = 24 * 60 * 60 * 1000;

function formatTickValue(val: unknown, format?: ChartValueFormat): string {
  const n = typeof val === "number" ? val : Number(val);
  if (!Number.isFinite(n)) return String(val ?? "");
  switch (format) {
    case "hourOfDay": {
      const h = Math.max(0, Math.min(24, Math.floor(n)));
      const m = Math.round((n - Math.floor(n)) * 60);
      return `${pad2(h)}:${pad2(m === 60 ? 0 : m)}`;
    }
    case "coordinate":
      return n.toFixed(3);
    case "score":
      return n.toFixed(2);
    default:
      return String(Math.round(n * 100) / 100);
  }
}

function formatTooltipValue(val: unknown, format?: ChartValueFormat): string {
  const n = typeof val === "number" ? val : Number(val);
  if (!Number.isFinite(n)) return String(val ?? "");
  switch (format) {
    case "hourOfDay": {
      const totalMin = Math.round(Math.max(0, Math.min(24, n)) * 60);
      const h = Math.floor(totalMin / 60);
      const m = totalMin % 60;
      return `${pad2(h)}:${pad2(m)} UTC`;
    }
    case "coordinate":
      return `${n.toFixed(5)}°`;
    case "score":
      return n.toFixed(3);
    case "seconds":
      return `${n} s`;
    case "minutes":
      return `${n} min`;
    case "hours":
      return `${n} h`;
    default:
      return String(n);
  }
}

function truncateCategoryLabel(val: unknown, maxLen = 18): string {
  const s = String(val ?? "");
  return s.length > maxLen ? `${s.slice(0, maxLen - 1)}…` : s;
}

interface CustomTooltipEntry {
  name?: string | number;
  value?: unknown;
  color?: string;
  payload?: unknown;
}

function ChartTooltipContent({
  active,
  payload,
  label,
  spec,
}: {
  active?: boolean;
  payload?: readonly CustomTooltipEntry[];
  label?: unknown;
  spec: ChartSpec;
}) {
  if (!active || !payload || payload.length === 0) return null;

  const isMultiSeries = (spec.series?.length ?? 0) > 1;
  const visibleEntries = isMultiSeries
    ? payload.filter((entry) => {
        const n = typeof entry.value === "number" ? entry.value : Number(entry.value);
        return !Number.isFinite(n) || n > 0 || payload.every((p) => Number(p.value) === 0);
      })
    : payload;

  const rawPayload = payload[0]?.payload;
  const rawRow =
    rawPayload && typeof rawPayload === "object"
      ? (rawPayload as Record<string, unknown>)
      : undefined;

  return (
    <div className="rounded-md border bg-white px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-medium text-[#1E293B]">{String(label ?? "")}</div>
      <div className="space-y-0.5">
        {visibleEntries.map((entry, idx) => (
          <div key={idx} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              {entry.color && (
                <span
                  className="inline-block size-2 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
              )}
              {entry.name ?? spec.yAxisLabel ?? "Value"}
            </span>
            <span className="font-medium text-[#1E293B]">
              {formatTooltipValue(entry.value, spec.valueFormat)}
            </span>
          </div>
        ))}
        {rawRow &&
          spec.tooltipMeta?.map((meta) => {
            const metaVal = rawRow[meta.key];
            if (metaVal === undefined || metaVal === null || metaVal === "") return null;
            return (
              <div key={meta.key} className="flex items-center justify-between gap-4 pt-0.5 border-t mt-1">
                <span className="text-muted-foreground">{meta.label}</span>
                <span className="font-medium text-[#1E293B]">
                  {meta.format ? formatTooltipValue(metaVal, meta.format) : String(metaVal)}
                </span>
              </div>
            );
          })}
      </div>
    </div>
  );
}

export function ChartCard({ spec }: { spec: ChartSpec }) {
  if (spec.type === "map") {
    return <LocationHeatmapCard title={spec.title} points={spec.points ?? []} />;
  }

  const defaultLabel =
    spec.type === "horizontal-bar" ? (spec.xAxisLabel ?? "Value") : (spec.yAxisLabel ?? "Value");
  const series = spec.series ?? [
    { key: "value", label: defaultLabel, color: "#2563EB", yAxisId: "left" as const },
  ];
  const hasRightAxis = series.some((s) => s.yAxisId === "right");
  const chartHeight =
    spec.type === "horizontal-bar" ? Math.max(300, spec.data.length * 28 + 44) : 300;

  return (
    <div className="rounded-md border bg-white p-4 shadow-xs">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-medium text-[#1E293B]">{spec.title}</h3>
        {(spec.xAxisLabel || spec.yAxisLabel) && (
          <span className="text-xs text-muted-foreground">
            {spec.xAxisLabel ? `x: ${spec.xAxisLabel}` : ""}
            {spec.xAxisLabel && spec.yAxisLabel ? " · " : ""}
            {spec.yAxisLabel ? `y: ${spec.yAxisLabel}` : ""}
            {spec.yRightAxisLabel ? ` / ${spec.yRightAxisLabel}` : ""}
          </span>
        )}
      </div>
      <ResponsiveContainer width="100%" height={chartHeight}>
        {spec.type === "line" ? (
          <LineChart data={spec.data} margin={{ top: 8, right: hasRightAxis ? 12 : 8, left: 4, bottom: 12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11 }}
              angle={-30}
              textAnchor="end"
              height={56}
            />
            <YAxis
              yAxisId="left"
              domain={spec.yDomain ?? [0, "auto"]}
              tick={{ fontSize: 11 }}
              width={52}
              tickFormatter={(v) => formatTickValue(v, spec.valueFormat)}
            />
            {hasRightAxis && (
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={spec.yRightDomain ?? ["auto", "auto"]}
                tick={{ fontSize: 11 }}
                width={56}
                tickFormatter={(v) => formatTickValue(v, spec.valueFormat)}
              />
            )}
            <Tooltip content={(props) => <ChartTooltipContent {...props} spec={spec} />} />
            {series.length > 1 && <Legend verticalAlign="top" height={28} />}
            {spec.referenceLines?.map((ref, idx) => (
              <ReferenceLine
                key={idx}
                yAxisId="left"
                y={ref.y}
                stroke={ref.color ?? "#10B981"}
                strokeDasharray="4 4"
                label={
                  ref.label
                    ? { value: ref.label, position: "insideTopRight", fontSize: 10, fill: ref.color ?? "#10B981" }
                    : undefined
                }
              />
            ))}
            {series.map((s) => {
              const dotsOnly = s.strokeWidth === 0;
              return (
                <Line
                  key={s.key}
                  yAxisId={s.yAxisId ?? "left"}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  stroke={dotsOnly ? "none" : s.color}
                  strokeWidth={s.strokeWidth ?? 2}
                  dot={
                    dotsOnly
                      ? { r: 4.5, fill: s.color, stroke: "#ffffff", strokeWidth: 1.5 }
                      : spec.showDots
                        ? { r: 3, fill: s.color }
                        : false
                  }
                  activeDot={{ r: 6, fill: s.color, stroke: "#ffffff", strokeWidth: 1.5 }}
                />
              );
            })}
          </LineChart>
        ) : spec.type === "area" ? (
          <AreaChart data={spec.data} margin={{ top: 8, right: 8, left: 4, bottom: 12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11 }}
              angle={spec.rotateLabels ? -30 : 0}
              textAnchor={spec.rotateLabels ? "end" : "middle"}
              height={spec.rotateLabels ? 56 : 36}
            />
            <YAxis
              domain={spec.yDomain ?? [0, "auto"]}
              tick={{ fontSize: 11 }}
              width={48}
              tickFormatter={(v) => formatTickValue(v, spec.valueFormat)}
            />
            <Tooltip content={(props) => <ChartTooltipContent {...props} spec={spec} />} />
            {series.length > 1 && <Legend verticalAlign="top" height={28} />}
            {series.map((s) => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stackId={s.stackId}
                stroke={s.color}
                strokeWidth={2}
                fill={s.color}
                fillOpacity={s.fillOpacity ?? 0.25}
                dot={spec.showDots ? { r: 3, fill: s.color } : false}
                activeDot={{ r: 5 }}
              />
            ))}
          </AreaChart>
        ) : spec.type === "horizontal-bar" ? (
          <BarChart
            data={spec.data}
            layout="vertical"
            margin={{ top: 6, right: 16, left: 8, bottom: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" />
            <XAxis
              type="number"
              domain={spec.yDomain ?? [0, "auto"]}
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => formatTickValue(v, spec.valueFormat)}
            />
            <YAxis
              type="category"
              dataKey="label"
              width={132}
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => truncateCategoryLabel(v, 19)}
            />
            <Tooltip content={(props) => <ChartTooltipContent {...props} spec={spec} />} />
            {series.length > 1 && <Legend verticalAlign="top" height={28} />}
            {series.map((s, idx) => {
              const isLastInStack = !s.stackId || idx === series.length - 1;
              return (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  name={s.label}
                  stackId={s.stackId}
                  fill={s.color}
                  radius={isLastInStack ? [0, 4, 4, 0] : [0, 0, 0, 0]}
                />
              );
            })}
          </BarChart>
        ) : (
          <BarChart data={spec.data} margin={{ top: 8, right: 8, left: 4, bottom: 12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11 }}
              angle={spec.rotateLabels ? -30 : 0}
              textAnchor={spec.rotateLabels ? "end" : "middle"}
              height={spec.rotateLabels ? 70 : 40}
            />
            <YAxis
              domain={spec.yDomain ?? [0, "auto"]}
              tick={{ fontSize: 11 }}
              width={48}
              tickFormatter={(v) => formatTickValue(v, spec.valueFormat)}
            />
            <Tooltip content={(props) => <ChartTooltipContent {...props} spec={spec} />} />
            {series.length > 1 && <Legend verticalAlign="top" height={28} />}
            {spec.referenceLines?.map((ref, idx) => (
              <ReferenceLine
                key={idx}
                y={ref.y}
                stroke={ref.color ?? "#10B981"}
                strokeDasharray="4 4"
                label={
                  ref.label
                    ? { value: ref.label, position: "insideTopRight", fontSize: 10, fill: ref.color ?? "#10B981" }
                    : undefined
                }
              />
            ))}
            {series.map((s, idx) => {
              const isLastInStack = !s.stackId || idx === series.length - 1;
              return (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  name={s.label}
                  stackId={s.stackId}
                  fill={s.color}
                  radius={isLastInStack ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                />
              );
            })}
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

interface VisualizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: PlotFilters;
  /** epoch ms captured when the button was clicked; anchors the rolling-24h default. */
  openedAt: number;
}

export function VisualizationDialog({ open, onOpenChange, filters, openedAt }: VisualizationDialogProps) {
  // Graphs default to a true rolling 24h (now-24h) when the user hasn't picked a range.
  const usingDefault = !filters.from;
  const effectiveFrom = filters.from || new Date(openedAt - DAY_MS).toISOString();

  const queryFilters = useMemo<PlotFilters>(
    () => ({ ...filters, from: effectiveFrom }),
    [filters, effectiveFrom],
  );

  const query = useQuery({
    queryKey: ["plot", queryFilters],
    queryFn: () => fetchPlotRecordsWithMeta(queryFilters),
    enabled: open,
  });

  const records = useMemo(() => query.data?.records ?? [], [query.data]);
  const charts = useMemo(
    () => buildCharts(records, filters.source ?? "", { from: effectiveFrom, to: filters.to }),
    [records, filters.source, effectiveFrom, filters.to],
  );
  const capped = Boolean(query.data?.truncated);
  const totalSlices = query.data?.totalSlices ?? 1;
  const loading = query.isLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[95vw] max-w-[1400px] max-h-[88vh] overflow-y-auto"
        onClose={() => onOpenChange(false)}
      >
        <DialogHeader>
          <DialogTitle>Visualization</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {filters.source || "All sources"} · {rangeLabel(filters, usingDefault)}
            {!loading && !query.isError && (
              <>
                {" · "}
                {records.length.toLocaleString()} records
                {capped
                  ? totalSlices > 1
                    ? ` (sampled across ${totalSlices} date segments)`
                    : " (capped)"
                  : ""}
              </>
            )}
          </p>
        </DialogHeader>

        {loading ? (
          <Spinner />
        ) : query.isError ? (
          <ErrorDisplay message={query.error.message} onRetry={() => query.refetch()} />
        ) : charts.length === 0 ? (
          <EmptyState message="No data to plot for these filters" />
        ) : (
          <div className={`grid gap-4 ${charts.length > 1 ? "lg:grid-cols-2" : "grid-cols-1"}`}>
            {charts.map((spec) => (
              <ChartCard key={spec.id} spec={spec} />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
